import React, { useState, useEffect } from 'react';
import DatabaseService from '../services/DatabaseService';

const SalesForm = ({ sale, onSave, onCancel, customers, batteries, chargers, quotationId = null }) => {
  // ตรวจสอบว่าการขายถูกปิดแล้วหรือไม่
  const isSaleClosed = sale && sale.status === 'สำเร็จ';
  const [formData, setFormData] = useState({
    sale_id: '',
    customer_id: '',
    sale_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    subtotal: 0,
    discount_amount: 0,
    total_amount: 0,
    status: 'รอดำเนินการ',
    notes: '',
    quotationId: quotationId
  });

  const [saleItems, setSaleItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [availableProducts, setAvailableProducts] = useState([]);

  useEffect(() => {
    // Combine batteries and chargers for product selection
    const products = [
      ...(batteries || []).filter(b => b.status === 'พร้อมขาย').map(b => ({
        ...b,
        product_type: 'battery',
        display_name: `${b.brand} ${b.model} (${b.voltage}V ${b.capacity}Ah)`,
        price: b.selling_price
      })),
      ...(chargers || []).filter(c => c.status === 'พร้อมขาย').map(c => ({
        ...c,
        product_type: 'charger',
        display_name: `${c.brand} ${c.model} (${c.output_voltage}V ${c.output_current}A)`,
        price: c.selling_price
      }))
    ];
    setAvailableProducts(products);
  }, [batteries, chargers]);

  useEffect(() => {
    const loadSaleData = async () => {
      if (sale && sale.id) {
        try {
          // ดึงข้อมูลการขายแบบเต็มจาก API
          console.log('Loading sale data for ID:', sale.id);
          const result = await DatabaseService.getSale(sale.id);
          console.log('API response:', result);
          
          if (result) {
            const saleData = result;
            console.log('Setting form data with:', saleData);
            
            setFormData({
              sale_id: saleData.sale_id || saleData.id || '',
              customer_id: saleData.customer_id || '',
              sale_date: saleData.sale_date || saleData.saleDate || new Date().toISOString().split('T')[0],
              delivery_date: saleData.delivery_date || saleData.deliveryDate || '',
              subtotal: saleData.subtotal || saleData.saleAmount || 0,
              discount_amount: saleData.discount_amount || 0,
              total_amount: saleData.total_amount || saleData.saleAmount || 0,
              status: saleData.status || 'รอดำเนินการ',
              notes: saleData.notes || '',
              quotationId: saleData.quotationId || quotationId
            });
            
            // Load sale items if editing
            if (saleData.items && Array.isArray(saleData.items)) {
              console.log('Loading sale items:', saleData.items);
              setSaleItems(saleData.items.map(item => ({
                id: item.id || Date.now() + Math.random(),
                product_id: item.product_id || '',
                product_type: item.product_type || '',
                product_name: item.product_name || item.serial_number || '',
                quantity: item.quantity || 1,
                unit_price: item.unit_price || 0
              })));
            } else {
              console.log('No items found or items is not an array');
              setSaleItems([]);
            }
          } else {
            console.log('No result from API');
          }
        } catch (error) {
          console.error('Error loading sale data:', error);
        }
      } else {
        // Generate new sale ID for new sale
        setFormData(prev => ({
          ...prev,
          sale_id: DatabaseService.generateSaleId(),
          quotationId: quotationId
        }));
      }
    };
    
    loadSaleData();
  }, [sale, quotationId]);

  useEffect(() => {
    calculateTotals();
  }, [saleItems, formData.discount_amount]);

  const calculateTotals = () => {
    const subtotal = saleItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const total = subtotal - formData.discount_amount;
    
    setFormData(prev => ({
      ...prev,
      subtotal: subtotal,
      total_amount: Math.max(0, total)
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'discount_amount' ? parseFloat(value) || 0 : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const addSaleItem = () => {
    setSaleItems(prev => [...prev, {
      id: Date.now(),
      product_id: '',
      product_type: '',
      product_name: '',
      quantity: 1,
      unit_price: 0
    }]);
  };

  const removeSaleItem = (index) => {
    setSaleItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateSaleItem = (index, field, value) => {
    setSaleItems(prev => {
      const newItems = [...prev];
      
      if (field === 'product_id') {
        const product = availableProducts.find(p => p.id === parseInt(value));
        if (product) {
          newItems[index] = {
            ...newItems[index],
            product_id: product.id,
            product_type: product.product_type,
            product_name: product.display_name,
            unit_price: product.price
          };
        }
      } else {
        newItems[index] = {
          ...newItems[index],
          [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value
        };
      }
      
      return newItems;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_id) {
      newErrors.customer_id = 'กรุณาเลือกลูกค้า';
    }

    if (!formData.sale_date) {
      newErrors.sale_date = 'กรุณาเลือกวันที่ขาย';
    }

    if (saleItems.length === 0) {
      newErrors.items = 'กรุณาเพิ่มสินค้าอย่างน้อย 1 รายการ';
    }

    saleItems.forEach((item, index) => {
      if (!item.product_id) {
        newErrors[`item_${index}_product`] = 'กรุณาเลือกสินค้า';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'กรุณากรอกจำนวนที่ถูกต้อง';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const saleData = {
        ...formData,
        quotationId: formData.quotationId || quotationId,
        items: saleItems.map(item => ({
          product_id: item.product_id,
          product_type: item.product_type,
          quantity: item.quantity,
          unit_price: item.unit_price
        }))
      };
      
      onSave(saleData);
    }
  };

  const selectedCustomer = customers?.find(c => c.id === parseInt(formData.customer_id));

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            {sale ? 'แก้ไขข้อมูลการขาย' : 'สร้างการขายใหม่'}
          </h5>
          {isSaleClosed && (
            <div className="d-flex gap-2">
              <span className="badge bg-success">{formData.status}</span>
              <span className="badge bg-danger">
                <i className="fas fa-lock me-1"></i>
                ปิดการขายแล้ว
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Sale Header */}
          <div className="row">
            <div className="col-md-4">
              <div className="form-group mb-3">
                <label htmlFor="sale_id" className="form-label">เลขที่ใบขาย</label>
                <input
                  type="text"
                  className="form-control"
                  id="sale_id"
                  name="sale_id"
                  value={formData.sale_id}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="form-group mb-3">
                <label htmlFor="sale_date" className="form-label">วันที่ขาย *</label>
                <input
                  type="date"
                  className={`form-control ${errors.sale_date ? 'is-invalid' : ''}`}
                  id="sale_date"
                  name="sale_date"
                  value={formData.sale_date}
                  onChange={handleChange}
                  disabled={isSaleClosed}
                  required
                />
                {errors.sale_date && <div className="invalid-feedback">{errors.sale_date}</div>}
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="form-group mb-3">
                <label htmlFor="delivery_date" className="form-label">วันที่ส่งมอบ (ถ้ามี)</label>
                <input
                  type="date"
                  className="form-control"
                  id="delivery_date"
                  name="delivery_date"
                  value={formData.delivery_date || ''}
                  onChange={handleChange}
                  disabled={isSaleClosed}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group mb-3">
                <label htmlFor="status" className="form-label">สถานะ</label>
                <select
                  className="form-control"
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={isSaleClosed}
                  required
                >
                  <option value="รอดำเนินการ">รอดำเนินการ</option>
                  <option value="สำเร็จ">สำเร็จ</option>
                  <option value="ยกเลิก">ยกเลิก</option>
                </select>
              </div>
            </div>
          </div>

          {/* Customer Selection */}
          <div className="row">
            <div className="col-md-12">
              <div className="form-group mb-3">
                <label htmlFor="customer_id" className="form-label">ลูกค้า *</label>
                <select
                  className={`form-control ${errors.customer_id ? 'is-invalid' : ''}`}
                  id="customer_id"
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                >
                  <option value="">เลือกลูกค้า</option>
                  {customers?.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.first_name} {customer.last_name}
                      {customer.company && ` (${customer.company})`}
                      {customer.phone && ` - ${customer.phone}`}
                    </option>
                  ))}
                </select>
                {errors.customer_id && <div className="invalid-feedback">{errors.customer_id}</div>}
                
                {selectedCustomer && (
                  <div className="mt-2 p-2 bg-light rounded">
                    <small>
                      <strong>ข้อมูลลูกค้า:</strong> {selectedCustomer.first_name} {selectedCustomer.last_name}<br/>
                      <strong>โทร:</strong> {selectedCustomer.phone}<br/>
                      {selectedCustomer.email && <><strong>อีเมล:</strong> {selectedCustomer.email}<br/></>}
                      {selectedCustomer.address && <><strong>ที่อยู่:</strong> {selectedCustomer.address}</>}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sale Items */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">รายการสินค้า</h6>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={addSaleItem}
                disabled={isSaleClosed}
              >
                <i className="fas fa-plus me-2"></i>
                เพิ่มสินค้า
              </button>
              {isSaleClosed && (
                <small className="text-muted ms-2">
                  <i className="fas fa-info-circle me-1"></i>
                  ไม่สามารถแก้ไขรายการสินค้าได้หลังปิดการขาย
                </small>
              )}
            </div>
            <div className="card-body">
              {errors.items && (
                <div className="alert alert-danger">{errors.items}</div>
              )}
              
              {saleItems.length === 0 ? (
                <p className="text-muted text-center">ยังไม่มีรายการสินค้า</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>สินค้า</th>
                        <th width="100">จำนวน</th>
                        <th width="120">ราคาต่อหน่วย</th>
                        <th width="120">รวม</th>
                        <th width="80">ลบ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {saleItems.map((item, index) => (
                        <tr key={item.id}>
                          <td>
                            <select
                              className={`form-control form-control-sm ${
                                errors[`item_${index}_product`] ? 'is-invalid' : ''
                              }`}
                              value={item.product_id}
                              onChange={(e) => updateSaleItem(index, 'product_id', e.target.value)}
                              disabled={isSaleClosed}
                            >
                              <option value="">เลือกสินค้า</option>
                              {availableProducts.map((product) => (
                                <option key={`${product.product_type}_${product.id}`} value={product.id}>
                                  [{product.product_type === 'battery' ? 'แบตเตอรี่' : 'เครื่องชาร์จ'}] {product.display_name} - {DatabaseService.formatCurrency(product.price)}
                                </option>
                              ))}
                            </select>
                            {errors[`item_${index}_product`] && (
                              <div className="invalid-feedback">{errors[`item_${index}_product`]}</div>
                            )}
                          </td>
                          <td>
                            <input
                              type="number"
                              className={`form-control form-control-sm ${
                                errors[`item_${index}_quantity`] ? 'is-invalid' : ''
                              }`}
                              value={item.quantity}
                              onChange={(e) => updateSaleItem(index, 'quantity', e.target.value)}
                              min="1"
                              step="1"
                              disabled={isSaleClosed}
                            />
                            {errors[`item_${index}_quantity`] && (
                              <div className="invalid-feedback">{errors[`item_${index}_quantity`]}</div>
                            )}
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.unit_price}
                              onChange={(e) => updateSaleItem(index, 'unit_price', e.target.value)}
                              step="0.01"
                              disabled={isSaleClosed}
                            />
                          </td>
                          <td>
                            <div className="text-end">
                              {DatabaseService.formatCurrency(item.quantity * item.unit_price)}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removeSaleItem(index)}
                              disabled={isSaleClosed}
                            >
                              ลบ
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Totals */}
          <div className="row">
            <div className="col-md-8">
              <div className="form-group mb-3">
                <label htmlFor="notes" className="form-label">หมายเหตุ</label>
                <textarea
                  className="form-control"
                  id="notes"
                  name="notes"
                  rows="3"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="กรอกหมายเหตุเพิ่มเติม"
                ></textarea>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-2">
                    <span>ยอดรวม:</span>
                    <span>{DatabaseService.formatCurrency(formData.subtotal)}</span>
                  </div>
                  
                  <div className="form-group mb-2">
                    <label htmlFor="discount_amount" className="form-label">ส่วนลด:</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      id="discount_amount"
                      name="discount_amount"
                      value={formData.discount_amount}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      max={formData.subtotal}
                      disabled={isSaleClosed}
                    />
                  </div>
                  
                  <hr/>
                  
                  <div className="d-flex justify-content-between">
                    <strong>ยอดสุทธิ:</strong>
                    <strong className="text-primary">
                      {DatabaseService.formatCurrency(formData.total_amount)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaleClosed}
            >
              {sale ? 'อัปเดต' : 'บันทึก'}
            </button>
            {isSaleClosed && (
              <small className="text-muted ms-2">
                <i className="fas fa-lock me-1"></i>
                ไม่สามารถแก้ไขข้อมูลได้หลังปิดการขาย
              </small>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesForm;